import { type CSSProperties } from "react";
import { ITINERARY_PANEL } from "../../../config/itineraryPanel.configuration";
import { type UIColorPalette } from "../../../theme/ColorPalette";

/**
 * Builds a layered vertical gradient with enough contrast to read on top of the map.
 * Brand tint fades from the top edge so walk/bike legs keep a neutral base below.
 *
 * @param palette Active UI palette for the current theme.
 * @param isDarkUi Whether dark map chrome is active.
 * @returns CSS `background` value with stacked gradients.
 */
export const buildItineraryCardGradientBackground = ( palette: UIColorPalette, isDarkUi: boolean ): string => {
    const brandAccentLayer = `linear-gradient(to bottom, ${ palette.selectorActiveBackgroundStrong } 0%, transparent 42%)`;

    if ( isDarkUi ) {
        return [
            brandAccentLayer,
            `linear-gradient(to bottom, ${ palette.overlayCardBackgroundStrong } 0%, ${ palette.selectorIdleBackground } 50%, ${ palette.overlayCardBackgroundSoft } 100%)`,
        ].join( ", " );
    }

    return [
        brandAccentLayer,
        "linear-gradient(to bottom, rgba(255, 255, 255, 0.97) 0%, rgba(241, 245, 249, 0.93) 45%, rgba(226, 232, 240, 0.86) 100%)",
    ].join( ", " );
};

/**
 * Resolves the CSS background for an itinerary list card.
 *
 * Solid mode uses the strong overlay token so card opacity stays visually stable across
 * the list: translucent fills sample different underlays (sibling cards vs map), which
 * made the last card (MyCar) look greyer than rows higher in the stack.
 *
 * @param palette Active UI palette for the current theme.
 * @param isDarkUi Whether dark map chrome is active.
 * @returns Either a gradient stack or a flat overlay color.
 */
export const resolveItineraryCardBackground = ( palette: UIColorPalette, isDarkUi: boolean ): string => {
    if ( ITINERARY_PANEL.itineraryCardBackground === "gradient" ) {
        return buildItineraryCardGradientBackground( palette, isDarkUi );
    }

    return palette.overlayCardBackgroundStrong;
};

/**
 * Builds the inline style object shared by itinerary list cards (including MyCar).
 *
 * @param palette Active UI palette for the current theme.
 * @param isDarkUi Whether dark map chrome is active.
 * @returns Background, border, and shadow styles for the card chrome.
 */
export const buildItineraryListCardStyle = ( palette: UIColorPalette, isDarkUi: boolean ): CSSProperties => ( {
    "background":  resolveItineraryCardBackground( palette, isDarkUi ),
    "borderColor": palette.overlayBorderStrong,
    "boxShadow":   isDarkUi
        ? "0 2px 10px rgba(0, 0, 0, 0.18)"
        : "0 2px 10px rgba(15, 23, 42, 0.06)",
} );

/**
 * Shared Tailwind class list for itinerary list card chrome (shape, border, padding, blur).
 */
export const ITINERARY_LIST_CARD_CHROME_CLASSNAME =
    "relative z-0 overflow-hidden rounded-xl border px-2 py-2 pb-1.5 backdrop-blur-md";
